import {
	ConsentStatus,
	ConsentType,
	FirebaseAnalytics,
} from '@capacitor-firebase/analytics';

import { Logger } from '@mopla/logger';

import type { TConsentCategories } from './cookieScript';
import { setUserIdConsent } from './firebaseUserId';

/**
 * Applies consent categories to the native Firebase Analytics SDK
 * (Google Consent Mode v2, advanced implementation).
 *
 * The categories come from the consent banner (see ./cookieScript); this
 * module only knows what they mean for Firebase. They are applied at app
 * start (stored decision) and again whenever the user changes them in the
 * banner, so a revocation takes effect right away — for future events only,
 * data already collected is not deleted.
 *
 * Advanced mode means collection stays enabled
 * (`firebase_analytics_collection_enabled`). While a consent type is denied
 * the SDK decides on its own what it may send: e.g. without
 * `analytics_storage` it sends cookieless pings without app instance id,
 * which GA4 uses for behavioural modelling. Nothing is dropped here.
 *
 * Defaults: all four types are denied in the native config
 * (AndroidManifest.xml `google_analytics_default_allow_*`, Info.plist
 * `GOOGLE_ANALYTICS_DEFAULT_ALLOW_*`). They have to live there and not here,
 * because the SDK starts — and logs `first_open` / `session_start` — before
 * the WebView and thus this code is running.
 *
 * Category mapping (CookieScript → Consent Mode):
 * - `performance` → `analytics_storage`
 * - `targeting`   → `ad_storage`, `ad_user_data`, `ad_personalization`
 * When the banner's categories are renamed, CONSENT_MAPPING has to follow.
 *
 * The user id is not covered by Consent Mode; the analytics decision is
 * passed on to ./firebaseUserId, which couples the id to it. On a denial the
 * id is cleared before setConsent, otherwise the SDK's consent-change event
 * still carries it; on a grant it is set after.
 */

const ANALYTICS_CATEGORY = 'performance';

const CONSENT_MAPPING: Record<string, ConsentType[]> = {
	[ANALYTICS_CATEGORY]: [ConsentType.AnalyticsStorage],
	targeting: [
		ConsentType.AdStorage,
		ConsentType.AdUserData,
		ConsentType.AdPersonalization,
	],
};

const logger = () => Logger.getInstance('UserTracking');

export const applyConsentToFirebase = async (
	categories: TConsentCategories
) => {
	const isAnalyticsGranted = categories.includes(ANALYTICS_CATEGORY);

	if (!isAnalyticsGranted) setUserIdConsent(false);

	try {
		await Promise.all(
			Object.entries(CONSENT_MAPPING).flatMap(([category, types]) => {
				const status = categories.includes(category)
					? ConsentStatus.Granted
					: ConsentStatus.Denied;
				return types.map((type) =>
					FirebaseAnalytics.setConsent({ type, status })
				);
			})
		);
	} catch (err) {
		logger().error('Firebase consent: setConsent failed', err);
	}

	if (isAnalyticsGranted) setUserIdConsent(true);

	logger().debug('Firebase consent: applied', { categories });
};
